import React, { useState, useEffect } from "react";
import {
  Modal,
  Paper,
  Grid,
  Box,
  TextField,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
  Typography,
  Divider,
  InputLabel,
  CircularProgress
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import { btnCss, ClsBtn, TextfieldCss } from "../../Styles"; 
import { isMobile } from "../Common";
import { loadProducts, syncCatalogAndProducts } from "../../API/ProductLayer"; 

function MetaCatalogModal({ open, handleClose, whatsappAccount }) {
  const [catalogName, setCatalogName] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [fetching, setFetching] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const hasCatalog = whatsappAccount?.commerce_catalog_id !== null && whatsappAccount?.commerce_catalog_id !== undefined;

  // Use your loadProducts function to get data when modal opens
  useEffect(() => {
    if (open) {
      setFetching(true);
      // Fetching without pagination to show all available for sync
      loadProducts("all=true").then((res) => {
        if (res) {
          // Adjust based on if your API returns {results: []} or just []
          setProducts(res.results || res);
          console.log("Loaded products for catalog sync:", res);
          setSelectedIds([]);
        }
        setFetching(false);
      });
    }
  }, [open]);

  const handleToggle = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSync = async () => {
    if (selectedIds.length === 0) return alert("Select at least one product.");
    if (!hasCatalog && !catalogName) return alert("Catalog name is required.");

    setSyncing(true);
    try {
      await syncCatalogAndProducts(whatsappAccount.id, selectedIds, catalogName);
      alert("Sync process initiated!");
      handleClose();
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setSyncing(false);
    }
  };

  const filteredProducts = products.filter((p) =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Modal open={open} onClose={handleClose}>
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: isMobile ? "90%" : 550,
          p: 4,
          outline: "none",
          borderRadius: "8px"
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            {hasCatalog ? "Update Meta Catalog" : "Meta Catalog Setup"}
          </Typography>
          <IconButton onClick={handleClose} sx={ClsBtn}>
            <CancelIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          {!hasCatalog && (
            <Grid item xs={12}>
              <InputLabel sx={{ mb: 1, fontWeight: 500 }}>Catalog Name</InputLabel>
              <TextField
                fullWidth
                placeholder="e.g. My Online Store"
                sx={TextfieldCss}
                value={catalogName}
                onChange={(e) => setCatalogName(e.target.value)}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <InputLabel sx={{ mb: 1, fontWeight: 500 }}>Select Products</InputLabel>
            <TextField
              fullWidth
              size="small"
              placeholder="Search product list..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 1 }}
            />
            
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                maxHeight: 250,
                overflowY: "auto",
                p: 1,
                backgroundColor: "#fafafa"
              }}
            >
              {fetching ? (
                <Box textAlign="center" p={2}><CircularProgress size={24} /></Box>
              ) : filteredProducts.length > 0 ? (
                filteredProducts.map((product) => (
                  <Box key={product.id} sx={{ borderBottom: "1px solid #eee" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!product.is_synced_to_catalog && selectedIds.includes(product.id)}
                          onChange={() => handleToggle(product.id)}
                          disabled={product.is_synced_to_catalog}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: product.is_synced_to_catalog ? '#888' : 'inherit' }}>
                          {product.name} <span style={{ color: '#888' }}>{product.is_synced_to_catalog && "(Synced to Catalog)"}</span>
                        </Typography>
                      }
                    />
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary" align="center" p={2}>
                  No products found.
                </Typography>
              )}
            </Box>
            <Typography variant="caption" color="primary">
              {selectedIds.length} items selected for sync
            </Typography>
          </Grid>

          <Grid item xs={12} mt={1}>
            <Button
              variant="contained"
              fullWidth
              sx={{ ...btnCss, backgroundColor: syncing ? "#ccc" : "#128C7E" }}
              onClick={handleSync}
              disabled={syncing || fetching}
            >
              {syncing ? "Processing..." : hasCatalog ? "Update Existing Catalog" : "Create & Sync Products"}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Modal>
  );
}

export default MetaCatalogModal;